import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import {
  ActionFailed,
  asLocalDate,
  Destroyable,
  positive,
  Rental,
  SaveForm,
  selectAirbnbLinkedRentals,
  selectRentalById,
  selectSomeOnce,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { asyncScheduler, BehaviorSubject, merge, Observable } from 'rxjs'
import * as R from 'ramda'
import moment from 'moment'
import { isUpdatingBooking, selectInquiry } from '@tv3/store/inquiry/inquiry.selectors'
import { filter, observeOn, switchMap } from 'rxjs/operators'
import { InquiryChargeService } from '@tv3/services/inquiry-charge.service'
import { DataCheckerService } from '@tokeet-frontend/tv3-platform'
import { PreferencesGuard } from '@tv3/guards/preferences.guard'
import { AirbnbChannelService, AirbnbReservationAlterationPriceQuotePayload } from '@tokeet-frontend/channels'
import { AirbnbReservationAlterationDialogService } from '@tv3/containers/inquiries/overlays/airbnb-reservation-alteration-dialog/airbnb-reservation-alteration-dialog.service'
import { InquirySourceType } from '@tv3/store/inquiry/inquiry-source-view'
import { TaskGuard } from '@tokeet-frontend/tasks'

@Component({
  selector: 'app-airbnb-create-alteration-dialog',
  templateUrl: './airbnb-create-alteration-dialog.component.html',
  host: { class: 'modal-content' },
  styleUrls: ['./airbnb-create-alteration-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AirbnbCreateAlterationDialogComponent extends Destroyable implements OnInit {
  inquiry: Inquiry

  timezone: string

  form = this.fb.group({
    rentalId: [null, [Validators.required]],
    guestArrive: [null, [Validators.required]],
    guestDepart: [null, [Validators.required]],
    adults: [null, [Validators.required]],
    children: [],
    infants: [],
    pets: [],
    listing_id: [],
    totalPrice: [null, [positive()]],
  })

  minDateDefault = moment().subtract(10, 'years').toDate()
  maxDateDefault = moment().add(10, 'years').toDate()

  sourceTypes = InquirySourceType

  minDepartDate = new BehaviorSubject<Date>(null)
  maxArriveDate = new BehaviorSubject<Date>(null)

  rentals$: Observable<Rental[]>
  isUpdatingBooking$ = this.store.pipe(select(isUpdatingBooking))

  adultsOptions = R.range(1, 31)
  childrenOptions = R.range(0, 31)

  constructor(
    public dialogRef: MatDialogRef<AirbnbCreateAlterationDialogComponent>,
    private fb: UntypedFormBuilder,
    private dataCheckerService: DataCheckerService,
    private airbnbChannelService: AirbnbChannelService,
    private airbnbReservationAlterationDialog: AirbnbReservationAlterationDialogService,
    private store: Store<fromRoot.State>,
    @Inject(MAT_DIALOG_DATA) public data: { inquiry: Inquiry }
  ) {
    super()
    this.dataCheckerService.check([PreferencesGuard, TaskGuard])
  }

  ngOnInit() {
    this.rentals$ = this.store.pipe(select(selectAirbnbLinkedRentals([this.data.inquiry.rentalId])))
    this.inquiry = this.data.inquiry
    this.store.pipe(selectSomeOnce(selectRentalById(this.inquiry.rentalId))).subscribe((rental) => {
      this.form.patchValue({
        listing_id: rental.abb_listing_id,
      })
    })

    this.setForm(this.inquiry)

    this.store.pipe(select(selectInquiry, { id: this.inquiry.id }), untilDestroy(this)).subscribe((inquiry) => {
      this.inquiry = inquiry
    })

    merge(this.form.get('guestArrive').valueChanges, this.form.get('guestDepart').valueChanges)
      .pipe(untilDestroy(this))
      .subscribe(() => {
        this.setArriveDepartMinMax()
      })
  }

  onRentalChange([rental]: [Rental]) {
    this.form.patchValue({
      listing_id: rental?.abb_listing_id,
    })
  }

  setForm(inquiry: Inquiry) {
    this.form.patchValue({
      rentalId: inquiry.rentalId,
      adults: inquiry.numAdults,
      children: inquiry.numChild,
      infants: inquiry.num_infants,
      pets: inquiry.num_pets,
      guestArrive: asLocalDate(inquiry.guestArrive),
      guestDepart: asLocalDate(inquiry.guestDepart),
    })
    this.setArriveDepartMinMax()
  }

  setArriveDepartMinMax() {
    if (this.form.get('guestArrive').value) {
      const arrive = moment(this.form.get('guestArrive').value) //.add(1, 'days')
      this.minDepartDate.next(arrive.toDate())
    }

    if (this.form.get('guestDepart').value) {
      const depart = moment(this.form.get('guestDepart').value) //.subtract(1, 'days')
      this.maxArriveDate.next(depart.toDate())
    }
  }

  tryToCreateAlteration() {
    const data = this.form.getRawValue()
    const payload: AirbnbReservationAlterationPriceQuotePayload = {
      start_date: moment(data.guestArrive).format('YYYY-MM-DD'),
      end_date: moment(data.guestDepart).format('YYYY-MM-DD'),
      number_of_adults: data.adults,
      number_of_children: data.children || 0,
      number_of_infants: data.infants || 0,
      number_of_pets: data.pets || 0,
      total_price_override: data.totalPrice,
      listing_id: data.listing_id,
    }

    return this.airbnbChannelService.getAlterationPriceQuote(this.inquiry.inquiryId, payload).pipe(
      switchMap((data) =>
        this.airbnbReservationAlterationDialog
          .open({ inquiry: this.inquiry, alteration: data, customTotalPrice: payload.total_price_override })
          .afterClosed()
      ),
      filter((r) => r === 'initiated')
    )
  }

  @SaveForm()
  onSave(form: UntypedFormGroup) {
    this.tryToCreateAlteration().subscribe(
      () => {
        this.dialogRef.close()
      },
      (error) => {
        this.store.dispatch(ActionFailed({ error }))
      }
    )
  }

  close() {
    this.dialogRef.close()
  }
}
